import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Loader2, Inbox, AlertTriangle, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Surface = ({ className, children, ...p }) => (
  <div
    className={cn(
      "tr-card relative backdrop-blur-xl grain",
      className
    )}
    {...p}
  >
    {children}
  </div>
);

export const PageHeader = ({ title, subtitle, action, testid }) => (
  <div
    data-testid={testid || "page-header"}
    className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between"
  >
    <div>
      <h1 className="text-3xl font-semibold text-foreground sm:text-4xl">
        {title}
        <span className="ml-3 hidden h-1.5 w-10 rounded-full bg-brand align-middle sm:inline-block" />
      </h1>
      {subtitle && <p className="mt-2 text-sm text-muted-foreground max-w-xl">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const Loading = ({ label = "Carregando...", testid = "loading-state" }) => (
  <div
    data-testid={testid}
    className="flex flex-col items-center justify-center gap-3 py-20 text-muted-foreground"
  >
    <Loader2 className="h-7 w-7 animate-spin text-brand" />
    <p className="text-sm">{label}</p>
  </div>
);

export const ErrorState = ({ message, onRetry, testid = "error-state" }) => (
  <Surface data-testid={testid} className="flex flex-col items-center gap-3 px-6 py-16 text-center">
    <AlertTriangle className="h-8 w-8 text-destructive" />
    <p className="text-sm text-muted-foreground max-w-sm">{message}</p>
    {onRetry && (
      <Button variant="outline" onClick={onRetry} data-testid="retry-btn" className="mt-2 rounded-full">
        Tentar novamente
      </Button>
    )}
  </Surface>
);

export const EmptyState = ({ title, description, action, icon: Icon = Inbox, testid = "empty-state" }) => (
  <Surface data-testid={testid} className="flex flex-col items-center gap-3 px-6 py-16 text-center">
    <div className="rounded-full border border-white/10 bg-secondary/60 p-4">
      <Icon className="h-7 w-7 text-muted-foreground" />
    </div>
    <h3 className="text-lg font-display font-medium">{title}</h3>
    {description && <p className="text-sm text-muted-foreground max-w-sm">{description}</p>}
    {action && <div className="mt-2">{action}</div>}
  </Surface>
);

export const Field = ({ label, hint, error, children, className }) => (
  <label className={cn("flex flex-col gap-1.5", className)}>
    <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
    {children}
    {hint && !error && <span className="text-xs text-muted-foreground">{hint}</span>}
    {error && <span className="text-xs text-destructive">{error}</span>}
  </label>
);

export const StatusPill = ({ tone = "info", children, testid, className }) => (
  <span data-testid={testid} className={cn("tr-status", `tr-status-${tone}`, className)}>
    {children}
  </span>
);

export const StockBadge = ({ low, testid }) => (
  <span
    data-testid={testid}
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
      low
        ? "border-destructive/40 bg-destructive/15 text-red-300"
        : "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
    )}
  >
    <span className={cn("h-1.5 w-1.5 rounded-full", low ? "bg-red-400" : "bg-emerald-400")} />
    {low ? "Estoque baixo" : "Estoque OK"}
  </span>
);

export const Thumb = ({ src, alt, className, fallback }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (src && !failed)
    return (
      <img
        key={src}
        src={src}
        alt={alt}
        onError={() => setFailed(true)}
        className={cn("h-12 w-12 shrink-0 rounded-md border border-white/10 object-cover", className)}
      />
    );
  return (
    <div
      className={cn(
        "flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-white/10 bg-secondary/60 text-xs font-num text-muted-foreground",
        className
      )}
    >
      {fallback || <Palette className="h-4 w-4 opacity-70" />}
    </div>
  );
};
